<script setup>
import { gsap } from "gsap";

const route = useRoute();
const router = useRouter();
const localePath = useLocalePath();
const { locale } = useI18n();
const query = gql`
query NewQuery {
  postBy(slug: "${route.params.id}") {

    translation: translation(language: ${locale.value.toString().toUpperCase()}) {
        title
        uri
        status
				slug

        blogConfig {
          perex
          content
          hidePublishedDate
          author {
						email
						intro
						name
						phone
						role
						image {
							altText
							id
							mediaItemUrl
						}
					}
					galleries {
						... on Gallery {
							id
							galleryId
							galleryConfig {
								images {
									mediaItemUrl
								}
							}
						}
					}
					video
					events {
						... on Event {
							id
							eventId
							date
							eventTags {
								edges {
									node {
										name
									}
								}
							}
							title
							uri
							slug
							eventsConfig {
								title
								perex
								featuredImage {
									mediaItemUrl
								}
								datesettings {
									timeEnd
									timeFrom
								}
							}
						}
					}
        }
        date
        author {
          node {
            id
            avatar {
              url
            }
            firstName
            lastName
          }
        }
        tags {
          edges {
            node {
              id
              name
            }
          }
        }
        featuredImage {
          node {
            mediaItemUrl
          }
        }
    }

    czechTranslation: translation(language: CS) {
			id
			title
			slug
			uri
		}

    englishTranslation: translation(language: EN) {
			id
			title
			slug
			uri
		}
  }
}
`;

const variables = { limit: 5 };
const { data } = await useAsyncQuery(query, variables);

if (data?.value?.postBy?.translation && route?.params?.id != data?.value?.postBy?.translation?.slug) {
  const correctUrl = route.fullPath.replace(
		/[^/]+\/?$/,
		`${data?.value?.postBy?.translation?.slug}/`
	);

  await navigateTo(correctUrl, {
    redirectCode: 301,
  });
}

if (!data?.value?.postBy || !data?.value?.postBy?.translation) {
  showError({ statusCode: 404, statusMessage: "Page Not Found" });
} else {
  router.push({
    path: localePath({
      name: "article-id",
      params: { id: data.value.postBy.translation.slug },
    }),
  });
}

const hidePublishedDate = data?.value?.postBy?.translation?.blogConfig?.hidePublishedDate ?? false;

const contentRef = ref(null);
const hasEn = data?.value?.postBy?.englishTranslation ? true : false;
const csSlug = data?.value?.postBy?.czechTranslation?.slug ?? null;
const enSlug = data?.value?.postBy?.englishTranslation?.slug ?? null;

const tags = data?.value?.postBy?.translation?.tags?.edges;
const title = data?.value?.postBy?.translation?.title;
const content = data?.value?.postBy?.translation?.blogConfig?.content;
const gallery = data?.value?.postBy?.translation?.blogConfig?.galleries;
const video = data?.value?.postBy?.translation?.blogConfig?.video;
const events = data?.value?.postBy?.translation?.blogConfig?.events;
const featuredImage =
  data?.value?.postBy?.translation?.featuredImage?.node?.mediaItemUrl;
const author = data?.value?.postBy?.translation?.blogConfig?.author?.name;
const authorIntro = data?.value?.postBy?.translation?.blogConfig?.author?.intro;
const authorPhone = data?.value?.postBy?.translation?.blogConfig?.author?.phone;
const authorEmail = data?.value?.postBy?.translation?.blogConfig?.author?.email;
const role = data?.value?.postBy?.translation?.blogConfig?.author?.role;
const authorImage =
  data?.value?.postBy?.translation?.blogConfig?.author?.image?.mediaItemUrl;

const parsedContent = content?.replace(/<img/g, '<img class="w-full"');

function replaceBlockquotesAndRemoveStyles(htmlContent) {
  // Create a DOM parser to parse the HTML content

  if (!htmlContent) return;
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, "text/html");

  // Remove styles from all elements
  const allElements = doc.querySelectorAll("*");
  allElements.forEach((element) => {
    element.removeAttribute("style");
  });

  // Find all blockquote elements
  const blockquotes = doc.querySelectorAll("blockquote");

  // Replace each blockquote element with the custom div structure
  blockquotes.forEach((blockquote) => {
    // Extract text content
    const quoteText = blockquote.innerText.trim();

    // Create the new element with the desired format
    const replacement = `
            <div class="quote">
                <div class="quote-sign">“</div>
                <div class="quote__text mb-12">
                    ${quoteText}
                </div>
                <div class="quote-sign">“</div>
            </div>
        `;

    // Replace the blockquote with the new element
    blockquote.outerHTML = replacement;
  });

  const pres = doc.querySelectorAll("pre");
  pres.forEach((pre) => {
    const content = pre.innerHTML;
    pre.outerHTML = content;
  });

  // Serialize the document back to a string and return it
  return doc.documentElement.innerHTML;
}

let finalContent;

const dateRaw = new Date(data?.value?.postBy?.translation?.date);
let perex;

const cursorHeight = ref(null);
watch(cursorHeight, (newVal) => {
  if (newVal > contentRef.value.getBoundingClientRect().height) return;
  if (
    newVal < contentRef.value.getBoundingClientRect().height &&
    newVal < getHeight.value
  ) {
    setCursor(null, null, {
      "font-size": "15px",
      background: "#FFF",
      width: "25px",
      height: "25px",
      "z-index": "1",
      position: "absolute",
    });
  }
  if (newVal < contentRef.value.getBoundingClientRect().height && newVal < 80) {
    setCursor(null, null, {
      "font-size": "14px",
      background: "#FFF",
      width: "25px",
      height: "25px",
      "z-index": "1",
      opacity: "0.7",
      position: "absolute",
    });
  }
  if (
    newVal < contentRef.value.getBoundingClientRect().height &&
    newVal > getHeight.value
  ) {
    setCursor(null, null, {
      "font-size": "14px",
      background: "#FFC800",
      width: "25px",
      height: "25px",
      "z-index": "1",
      position: "absolute",
    });
  }
});

const formatDate =
  ("0" + dateRaw.getDate()).slice(-2) +
  "." +
  ("0" + (dateRaw.getMonth() + 1)).slice(-2) +
  "." +
  dateRaw.getFullYear();

const getDate = (
	dateString,
	timeString = null,
	returnISOTime = false
) => {
	const date = new Date(dateString);

	if (returnISOTime && timeString) {
		const [hours, minutes] = timeString.split(":");

		date.setHours(
			parseInt(hours, 10),
			parseInt(minutes, 10),
			0,
			0
		);

		const pad = (value) => String(value).padStart(2, "0");

		const timeZone = "Europe/Prague";

		const formatter = new Intl.DateTimeFormat("en-US", {
			timeZone,
			timeZoneName: "longOffset",
		});

		const parts = formatter.formatToParts(date);

		const timeZoneName = parts.find(
			(part) => part.type === "timeZoneName"
		)?.value;

		const offset = timeZoneName?.replace("GMT", "") || "+00:00";

		return `${date.getFullYear()}-${pad(
			date.getMonth() + 1
		)}-${pad(date.getDate())}T${pad(
			date.getHours()
		)}:${pad(date.getMinutes())}:00${offset}`;
	}

	return `${date.getDate() < 10 ? "0" : ""}${date.getDate()}.${
		date.getMonth() + 1 < 10 ? "0" : ""
	}${date.getMonth() + 1}.${date.getFullYear()}`;
};

const updateWidth = () => {
  width.value =
    document.body.clientWidth ||
    window.innerWidth ||
    document.documentElement.clientWidth;
  if (width.value > 1024) {
    gsap.set("#cursor", { xPercent: -50, yPercent: -50 });

    xTo.value = gsap.quickTo("#cursor", "x", { duration: 0.1, ease: "power3" });
    yTo.value = gsap.quickTo("#cursor", "y", { duration: 0.1, ease: "power3" });
  }
};

const width = ref(null);
const xTo = ref(null);
const yTo = ref(null);

const heroContent = ref(null);
const heroHeight = ref(0);
const heroImgContent = ref(null);
const heroImgHeight = ref(0);

const upcomingEvents = ref(true)

onMounted(() => {
  width.value =
    document.body.clientWidth ||
    window.innerWidth ||
    document.documentElement.clientWidth;
  if (width.value > 1024) {
    gsap.set("#cursor", { xPercent: -50, yPercent: -50 });

    xTo.value = gsap.quickTo("#cursor", "x", { duration: 0.1, ease: "power3" });
    yTo.value = gsap.quickTo("#cursor", "y", { duration: 0.1, ease: "power3" });
  }

  window.addEventListener("resize", updateWidth);

  finalContent = replaceBlockquotesAndRemoveStyles(parsedContent);
  perex = replaceBlockquotesAndRemoveStyles(
    data?.value?.postBy?.translation?.blogConfig?.perex
  );

  if (heroContent.value) {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        heroHeight.value = entry.contentRect.height;
      }
    });

    resizeObserver.observe(heroContent.value);

    // Ukládáme observer do ref, abychom ho mohli později zrušit
    heroContent.__resizeObserver = resizeObserver;
  }
  if (heroImgContent.value) {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        heroImgHeight.value = entry.contentRect.height;
      }
    });

    resizeObserver.observe(heroImgContent.value);

    // Ukládáme observer do ref, abychom ho mohli později zrušit
    heroImgContent.__resizeObserver = resizeObserver;
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateWidth);
  window.document.removeEventListener("click", cursorOnClick.value);

  if (heroContent.__resizeObserver) {
    heroContent.__resizeObserver.unobserve(heroContent.value);
  }
  if (heroImgContent.__resizeObserver) {
    heroImgContent.__resizeObserver.unobserve(heroImgContent.value);
  }
});

const getHeight = computed(() => {
  return heroHeight.value + 143 - heroImgHeight.value / 2;
});

const futureOrderedEvents= computed(() => {
	const fEvents = [...events].filter(event => new Date(event.date) > new Date())

	const oEvents = [...fEvents].sort(function(a,b){
		return new Date(a.date) - new Date(b.date);
	});

	return oEvents
})

const pastOrderedEvents= computed(() => {
	const fEvents = [...events].filter(event => new Date(event.date) <= new Date())

	const oEvents = [...fEvents].sort(function(a,b){
		return new Date(b.date) - new Date(a.date);
	});

	return oEvents
})

const setCursor = (value, onClick, style) => {
  cursor.value = value;
  if (cursorOnClick.value !== onClick) {
    window.document.removeEventListener("click", cursorOnClick.value);
    cursorOnClick.value = onClick;

    window.document.addEventListener("click", cursorOnClick.value);
  }
  if (cursorStyle.value !== style) {
    cursorStyle.value = style;
  }
};

const cursor = ref("");
const cursorStyle = ref({});
const cursorOnClick = ref(() => {});
const mouseMove = (e) => {
  if (width.value > 1024) {
    xTo.value(e.pageX);
    yTo.value(e.pageY);

    cursorHeight.value = e.pageY;
  }
};

const meta = ref({});
if (data.value && data?.value?.postBy?.translation?.title) {
  meta.value = {
    title: `Opero | ${data?.value?.postBy?.translation?.title}`,
    description: data?.value?.postBy?.translation?.blogConfig?.perex,
    image: featuredImage,
    url: route.fullPath,
    csUrl: csSlug ? `cs/blog/${csSlug}` : null,
    enUrl: enSlug ? `en/blog/${enSlug}` : null,
  };

  useHeadMeta({ ...meta.value }, [{
    "@id": "#article",
    "@type": "Article",
    "headline": data?.value?.postBy?.translation?.title,
    "description": data?.value?.postBy?.translation?.blogConfig?.perex,
    "image": featuredImage,
    "datePublished": getDate(`${dateRaw.getFullYear()}-${dateRaw.getMonth()+1}-${dateRaw.getDate()}`, '00:00:00', true),
    "publisher": {
      "@id": "#company",
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://www.domain.com/blog/your-article",
    },
		}]);
}
</script>

<template>
  <div>
    <div @mousemove="mouseMove">
      <div
        class="event-detail-header lg:pt-0"
        :style="{ height: getHeight + 'px' }"
      >
        <div class="navbar-wrapper">
          <Navbar :is-dark="true" :is-white="true" :has-en="hasEn" en-list-page="/en/blog" :cs-slug="csSlug" :en-slug="enSlug" />
        </div>
        <div
          v-if="data"
          ref="contentRef"
          class="flex justify-center mt-28 lg:mt-36 relative z-40 overflow-hidden lg:pb-36 pb-20"
        >
          <div class="container px-6">
            <div ref="heroContent">
              <div class="flex flex-wrap gap-2 pb-6">
                <div
                  v-for="(tag, index) in tags"
                  :key="index"
                  class="event-tag !bg-white"
                >
                  {{ tag?.node?.name }}
                </div>
              </div>
              <h1>{{ title }}</h1>
              <div class="flex pt-8 pb-4">
                <b v-if="hidePublishedDate == false" class="pr-1">{{ formatDate }}</b>
              </div>
              <div class="pb-12 text-2xl">
                <div v-if="perex" v-html="perex"></div>
              </div>
              <div class="flex justify-center w-full">
                <div class="event-detail-ftr-img">
									<div v-if="video">
										<video
											
											autoplay
											loop
											muted
											playsinline
										>
											<source
												:src="video"
												type="video/mp4"
											/>
											Your browser does not support the video tag.
										</video>
									</div>
                  <img v-else ref="heroImgContent" :src="featuredImage" />
                  <div
                    class="absolute -right-12 top-0 flex flex-col gap-5 mt-12 lg:flex w-6"
                  >
                    <Socials :title="title" />
                  </div>
                </div>
              </div>
            </div>

            <div class="flex justify-center pt-12 event-content wysiwyg">
              <div v-html="finalContent"></div>
            </div>

            <div class="flex gap-5 mb-12 mt-12 lg:hidden">
              <Socials :title="title" />
            </div>

            <div v-if="author">
              <b>{{ authorIntro? authorIntro : $t("articlePublishedBy") }}</b>
              <div class="flex items-center mt-4">
                <div>
                  <img
                    v-if="authorImage"
                    :src="authorImage"
                    class="rounded-full object-cover"
										:class="[authorIntro ? 'w-24 h-24' : 'w-16 h-16']"
                  />
                </div>
                <div class="ml-4">
                  <div class="txt-q">{{ author }}</div>
                  <div class="font-bold">{{ role }}</div>
									<div v-if="authorEmail" class="font-bold"><a class="hover:underline" :href="`mailto:${authorEmail}`">{{ authorEmail }}</a></div>
									<div v-if="authorPhone" class="font-semibold"><a class="hover:underline" :href="`tel:${authorPhone}`">{{ authorPhone }}</a></div>
                </div>
              </div>
            </div>
          </div>
        </div>
				<div class="container px-6 mx-auto">
					<EventGallery
						:gallery="gallery[0].galleryConfig.images"
						v-if="gallery"
					/>
				</div>

        <BlogRelated
          v-if="tags && tags.length > 0"
          :set-cursor="setCursor"
          :tags="tags"
        />

				<div class="max-w-7xl w-full px-6 mx-auto my-28" v-if="events">
					<h1 class="text-center mb-8">{{ $t('events') }}</h1>
					<div class="mb-8">
						<a
							class="mr-6 uppercase"
							:class="[upcomingEvents ? 'font-bold' : 'font-light underline hover:no-underline']"
							href="#"
							@click.prevent="upcomingEvents = true"
							>{{ $t("upcoming") }}
						</a>
						<a
							class="uppercase"
							:class="[!upcomingEvents ? 'font-bold' : 'font-light underline hover:no-underline']"
							href="#"
							@click.prevent="upcomingEvents = false"
							>{{ $t("events.tag.events.pastEvents") }}
						</a>
					</div>
					<div v-if="upcomingEvents">
						<CalendarEvent
							v-for="event in futureOrderedEvents"
							:key="event.id"
							:data="event"
							class="transition-opacity duration-500"
							:class="{ 'opacity-20': loading, 'opacity-20': filtering }"
						/>
					</div>
					<div v-else>
						<CalendarEvent
							v-for="event in pastOrderedEvents"
							:key="event.id"
							:data="event"
							class="transition-opacity duration-500"
							:class="{ 'opacity-20': loading, 'opacity-20': filtering }"
						/>
					</div>
				</div>

        <div v-if="false">
        <PartnersWrapper v-if="!events" :set-cursor="setCursor" />
        </div>
        <BecomeMember :set-cursor="setCursor" />
        <Footer :set-cursor="setCursor" />
      </div>

      <div></div>
    </div>
    <div class="blog-header">
      <div v-if="data" class="flex justify-center">
        <div class="max-w-7xl">
          <!--                        {{ formatDate }}-->
          <!--                        <h1> {{ title }}</h1>-->
          <!--                        <div class="flex justify-center">-->
          <!--                              <div v-html="content"></div>-->
          <!--                        </div>-->
        </div>
      </div>
    </div>
    <div
      id="cursor"
      class="hidden pointer-events-none cursor-auto lg:cursor-none lg:flex"
      :style="cursorStyle"
      :class="{
        smallCursor: cursor === null,
        hidden: !width || width < 1024,
      }"
      v-html="cursor"
    ></div>
  </div>
</template>

<style scoped lang="scss">
h1 {
  line-height: normal;
}
.container {
  width: 100vw;
  max-width: 840px;
}
ul {
  font-size: 20px;
  margin-left: 20px;
  list-style-type: disc;
  list-style-position: outside;

  display: block;
  margin-block-start: 1em;
  margin-block-end: 1em;
  margin-inline-start: 0px;
  margin-inline-end: 0px;
  padding-inline-start: 1em;
}
.event-detail-header {
  background-color: $color-primary;
  height: 100vh;

  p {
    padding: 2rem 0;
    //max-width: 600px;
  }

  h3 {
    font-size: 28px;
    font-weight: 300;
  }
}

.event-detail-ftr-img {
  position: relative;

  img, video {
    width: 1200px;
    max-width: 1200px;
  }

  @media screen and (max-width: 1300px) {
    img {
      width: 900px;
      max-width: 900px;
    }
  }
  @media screen and (max-width: 1000px) {
    img {
      width: 100%;
      max-width: 100%;
    }
  }
}

.event-tag {
  background: $color-primary;
  border-radius: 100px;
  padding: 10px 20px;
  font-weight: 500;
  font-size: 16px;
  font-weight: 400;
  height: 40px;
  white-space: nowrap;

  display: inline-flex;
  padding: 10px 20px;
  justify-content: center;
  align-items: center;
  gap: 10px;
}

#cursor {
  position: absolute;
  top: 0;
  left: -10px;
  width: 25px;
  height: 25px;
  font-weight: 400;

  justify-content: center;
  align-items: center;
  background-color: $color-primary;
  border-radius: 50%;
  z-index: 1000;
  cursor: none;
  transition:
    width 0.3s,
    height 0.3s,
    background-color 0.3s;
  transform: translate(-50%, -50%) translate(-150px, -150px);
  //pointer-events: none;
  text-align: center;
  display: none;
  @media screen {
    @media (pointer: fine) {
      display: flex;
    }
  }
}

.smallCursor {
  width: 20px;
  height: 20px;
  background-color: $color-primary;
}

.negative-margin {
  @media screen and (max-width: 450px) {
    margin-left: -7px;
  }
  //margin-top: -9px;
  //margin-bottom: -9px;
}

.perex {
  color: #1a1a1a;
  font-family: Roboto;
  font-size: 26px;
  font-style: normal;
  font-weight: 300;
  line-height: 28px; /* 140% */
}
</style>

<style>
.event-content {
  a {
    text-decoration: underline;
  }
  p {
    font-size: 20px;
  }

  .quote-sign {
    color: #ffc800;
    font-family: Roboto;
    font-size: 60px !important;
    font-style: normal;
    font-weight: 700;
    line-height: normal;

    text-align: center;
  }

  .quote__text {
    color: #1a1a1a;
    text-align: center;
    font-family: Roboto;
    font-size: 32px !important;
    font-style: normal;
    font-weight: 300;
    line-height: normal;
  }
}
</style>
